/**
 * Tournament card component
 * Displays tournament information in a card format
 */

import { Calendar, Trophy, Users, Wallet } from 'lucide-react';
import { useRouter } from 'next/navigation';
import type { Tournament } from '@/lib/types/auction';
import { formatDate, formatCurrency } from '@/lib/utils/format';
import { TournamentActions } from './TournamentActions';
import { TournamentStatusBadge } from './TournamentStatusBadge';
import { Card } from '@/components/common/Card';
import { motion } from 'framer-motion';

interface TournamentCardProps {
  tournament: Tournament;
  onAction: (actionType?: string) => void;
  variant?: 'simple' | 'full';
}

export function TournamentCard({
  tournament,
  onAction,
  variant = 'full',
}: TournamentCardProps) {
  const router = useRouter();
  const progress = {
    teamsRegistered: tournament.teams.length,
    teamsPercentage: (tournament.teams.length / tournament.totalTeams) * 100,
    playersRegistered: tournament.totalEnrollments,
    playersPercentage:
      (tournament.totalEnrollments /
        (tournament.totalTeams * tournament.maxPlayersPerTeam)) *
      100,
    playersBought: tournament.teams.reduce(
      (acc, team) => acc + team.playerCount,
      0
    ),
    totalPlayers: tournament.totalTeams * tournament.maxPlayersPerTeam,
    playersBoughtPercentage:
      (tournament.teams.reduce((acc, team) => acc + team.playerCount, 0) /
        (tournament.totalTeams * tournament.maxPlayersPerTeam)) *
      100,
  };

  const handleCardClick = () => {
    router.push(`/tournament/${tournament.id}`);
  };

  const isSimpleVariant = variant === 'simple';

  return (
    <Card
      allowOverflow
      className="cursor-pointer backdrop-blur-none"
      onClick={handleCardClick}
    >
      <div className="relative p-2">
        <div className="flex flex-col items-start justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
          <div className="w-full flex-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-primary-500 to-accent-blue text-white shadow-sm">
                  <Trophy className="h-4 w-4" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {tournament.name}
                </h3>
              </div>
              <div className="flex justify-end gap-2">
                <TournamentStatusBadge status={tournament.status} />
                <div
                  className="relative flex flex-shrink-0 items-center justify-center space-x-2 sm:w-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TournamentActions
                    tournament={tournament}
                    onAction={onAction}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description - Only in full variant */}
        {!isSimpleVariant && tournament.description && (
          <p className="mt-3 line-clamp-2 text-sm text-gray-500">
            {tournament.description}
          </p>
        )}

        {/* Stats Section */}
        {isSimpleVariant ? (
          <div className="mt-4 space-y-1">
            <div className="flex items-center text-sm text-gray-600">
              <Users className="mr-2 h-4 w-4 text-accent-blue" />
              <span>
                Teams: {progress.teamsRegistered} / {tournament.totalTeams}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="mr-2 h-4 w-4 text-gray-400" />
              <span>Starts: {formatDate(tournament.startDate)}</span>
            </div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-2 transition-colors hover:bg-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-accent-blue" />
                  <p className="text-sm font-medium text-gray-700">Teams</p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-semibold text-gray-900">
                    {progress.teamsRegistered}
                  </span>
                  <span className="ml-1 text-sm text-gray-500">
                    / {tournament.totalTeams}
                  </span>
                </div>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress.teamsPercentage}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className="h-full rounded-full bg-gradient-to-r from-accent-blue to-accent-purple"
                >
                  <div className="h-full w-full animate-shimmer bg-white/20"></div>
                </motion.div>
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-2 transition-colors hover:bg-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-accent-purple" />
                  <p className="text-sm font-medium text-gray-700">
                    Players Registered
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-semibold text-gray-900">
                    {progress.playersRegistered}
                  </span>
                  <span className="ml-1 text-sm text-gray-500">
                    / {progress.totalPlayers}
                  </span>
                </div>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress.playersPercentage}%` }}
                  transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                  className="h-full rounded-full bg-gradient-to-r from-accent-purple to-accent-green"
                >
                  <div className="h-full w-full animate-shimmer bg-white/20"></div>
                </motion.div>
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-2 transition-colors hover:bg-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Trophy className="h-5 w-5 text-accent-green" />
                  <p className="text-sm font-medium text-gray-700">
                    Players Bought
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-semibold text-gray-900">
                    {progress.playersBought}
                  </span>
                  <span className="ml-1 text-sm text-gray-500">
                    / {progress.totalPlayers}
                  </span>
                </div>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress.playersBoughtPercentage}%` }}
                  transition={{ duration: 1, ease: 'easeOut', delay: 0.4 }}
                  className="h-full rounded-full bg-gradient-to-r from-accent-green to-accent-blue"
                >
                  <div className="h-full w-full animate-shimmer bg-white/20"></div>
                </motion.div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Info - Only in full variant */}
        {!isSimpleVariant && (
          <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-4 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-1.5">
              <Wallet className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Base Budget:</span>
              <span className="font-medium text-gray-700">
                {formatCurrency(tournament.baseBudget)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Auction Date:</span>
              <span className="font-medium text-gray-700">
                {formatDate(tournament.auctionDate)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Tournament Dates:</span>
              <span className="font-medium text-gray-700">
                {formatDate(tournament.startDate)} -{' '}
                {formatDate(tournament.endDate)}
              </span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
